
import { useEffect, useState } from 'react'; 
import { formatBytes } from '@/utils/formatBytes';

import { Document, Page, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface DocumentPreviewPanelProps {
  file: File;
  fileName: string;
  fileSizeBytes: number;
  totalPages: number;
  onChangeDocument: () => void;
}

const ZOOM_MIN = 60;
const ZOOM_MAX = 160;
const ZOOM_STEP = 20;

export function DocumentPreviewPanel({
  file,
  fileName,
  fileSizeBytes,
  totalPages,
  onChangeDocument,
}: DocumentPreviewPanelProps) {
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [isLandscape, setIsLandscape] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);

    setFileUrl(url);
    setPage(1);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const isPdf = file.type === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');

  const previewWidth = isLandscape ? 520 : 460;
  const scaledWidth = previewWidth * (zoom / 100);

  return (
    <section className="bg-surface-lowest rounded-4 shadow-kiosk-md p-4 d-none d-lg-flex flex-column">
      <div className="d-flex align-items-center justify-content-between pb-3">
        <div className="d-flex align-items-center gap-3 min-w-0">
          <i
            className="bi bi-file-earmark-text text-primary"
            style={{ fontSize: 26 }}
          />

          <div className="min-w-0">
            <h4
              className="text-label-lg text-on-surface mb-1"
              title={fileName}
              style={{
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {fileName}
            </h4>

            <p className="text-body-sm text-on-surface-variant mb-0">
              {totalPages} Pages • {formatBytes(fileSizeBytes)} • Actual document preview
            </p>
          </div>
        </div>

        <div className="d-flex align-items-center gap-1 bg-surface-low p-1 rounded-3 flex-shrink-0">
          <button
            type="button"
            className="btn tap-target active-press border-0 text-on-surface-variant"
            onClick={() =>
              setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))
            }
            title="Zoom out"
          >
            <i className="bi bi-zoom-out" />
          </button>

          <span className="text-label-sm fw-semibold px-2 text-on-surface">
            {zoom}%
          </span>

          <button
            type="button"
            className="btn tap-target active-press border-0 text-on-surface-variant"
            onClick={() =>
              setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))
            }
            title="Zoom in"
          >
            <i className="bi bi-zoom-in" />
          </button>

          <button
            type="button"
            className="btn tap-target active-press border-0 text-on-surface-variant"
            onClick={() => setZoom(100)}
            title="Fit to viewport"
          >
            <i className="bi bi-arrows-fullscreen" />
          </button>
        </div>
      </div>

      <div
        className="position-relative bg-surface-low rounded-4 d-flex align-items-center justify-content-center overflow-auto"
        style={{ height: 460 }}
      >
        {isPdf && fileUrl ? (
          <Document
            file={fileUrl}
            loading={
              <div className="text-body-sm text-on-surface-variant">
                Loading document preview...
              </div>
            }
            error={
              <div className="text-body-sm text-danger">
                Unable to preview this document.
              </div>
            }
          >
            <Page
              pageNumber={page}
              width={scaledWidth}
              rotate={isLandscape ? 90 : 0}
              renderTextLayer
              renderAnnotationLayer
            />
          </Document>
        ) : file.type.startsWith('image/') ? (
          <img
            src={fileUrl ?? ''}
            alt={`Page ${page} of ${fileName}`}
            className="bg-surface-lowest shadow-lg rounded"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `scale(${zoom / 100})`,
              transition: 'transform 0.2s ease',
            }}
          />
        ) : (
          <div className="text-center p-4">
            <i
              className="bi bi-file-earmark-text text-primary"
              style={{ fontSize: 48 }}
            />

            <p className="text-body-md text-on-surface fw-semibold mt-3 mb-1">
              Preview not available
            </p>

            <p className="text-body-sm text-on-surface-variant mb-0">
              This file type can still be printed, but a browser preview is
              not available.
            </p>
          </div>
        )}

        <button
          type="button"
          className="btn position-absolute top-50 start-0 translate-middle-y ms-3 bg-surface-lowest bg-opacity-90 shadow rounded-circle tap-target active-press border-0"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          title="Previous page"
        >
          <i className="bi bi-chevron-left" />
        </button>

        <button
          type="button"
          className="btn position-absolute top-50 end-0 translate-middle-y me-3 bg-surface-lowest bg-opacity-90 shadow rounded-circle tap-target active-press border-0"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          title="Next page"
        >
          <i className="bi bi-chevron-right" />
        </button>
      </div>

      <div className="d-flex align-items-center justify-content-between pt-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-label-md text-on-surface fw-semibold">
            Viewing Page:
          </span>

          <span className="px-2 py-1 bg-surface-container rounded text-label-md fw-bold text-primary">
            {page}
          </span>

          <span className="text-label-md text-on-surface-variant">
            of {totalPages} pages
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm bg-surface-container text-on-surface active-press border-0"
            onClick={() => setIsLandscape((v) => !v)}
          >
            Rotate 90°
          </button>

          <button
            type="button"
            className="btn btn-sm bg-surface-container text-on-surface active-press border-0"
            onClick={() => setZoom(100)}
          >
            Fit Width
          </button>
        </div>
      </div>

      <button
        type="button"
        className="btn text-primary text-label-md active-press border-0 mt-2 align-self-start d-flex align-items-center gap-2"
        onClick={onChangeDocument}
      >
        <i className="bi bi-arrow-repeat" />
        Change document
      </button>
    </section>
  );
}

